    /*
    Static function which is purely functional and used in other places.
    Class encapsulates function without data. can be referenced as:
    FuncMinutes2HourFormat.Minutes2Format(mins); in other parts without
    instantiating an object of the FuncMinutes2HourFormat class.
    */

export type MinutesOutputFormat = 'HH:MM' | 'MM:SS' | 'HH:MM:SS' | 'long' | 'short' | 'abbr';
export type HoursOutputFormat = 'MM:SS' | 'HH:MM:SS';
export type DaysOutputFormat = 'HH:MM:SS';
export type SecondsOutputFormat = 'MM:SS' | 'HH:MM:SS';

export class NegativeTimeException {

};
// Class Taken From Teacher Portal
export class FuncMinutes2Format {

    static Minutes2Format_HH_MM(mins: number) {
        if (mins < 0) {
            throw new Error('Negative Time Exception');
        } else {
            let format = '';
            const round = Math.floor(mins / 60);
            const left = mins - (round * 60);
            format = '' + round + ':' + this.AddLeadingZeroToSingleMinutesAndSeconds(left);
            return format;
        }
    }

    static Minutes2Format_Long(mins: number) {
        const hours = Math.floor(mins / 60);
        const minutes = mins % 60;

        // might need to worry about other languages - currently this is in english pluralization pattern

        if (hours > 1) {
            if (minutes == 1) {
                return $localize`:@@CommonDurationDescriptionXHoursOneMinute:${hours} hours 1 minute`;
            } else if (minutes > 1) {
                return $localize`:@@CommonDurationDescriptionXHoursXMinutes:${hours} hours ${minutes} minutes`;
            } else if (!minutes) {
                return $localize`:@@CommonDurationDescriptionXHours:${hours} hours`;
            }
        } else if (hours == 1) {
            if (minutes == 1) {
                return $localize`:@@CommonDurationDescriptionOneHourOneMinute:1 hour 1 minute`;
            } else if (minutes > 1) {
                return $localize`:@@CommonDurationDescriptionOneHourXMinutes:1 hour ${minutes} minutes`;
            } else if (!minutes) {
                return $localize`:@@CommonDurationDescriptionOneHour:1 hour`;
            }
        } else if (!hours) {
            if (minutes == 1) {
                return $localize`:@@CommonDurationDescriptionOneMinute:1 minute`;
            } else if (minutes > 1) {
                return $localize`:@@CommonDurationDescriptionXMinutes:${minutes} minutes`;
            } 
        }
        return $localize`:@@CommonZeroMinutes:0 minutes`;
    }

    static Minutes2Format_Short(mins: number) {
        const hours = Math.floor(mins / 60);
        const minutes = mins % 60;

        if (hours && minutes) {
            return $localize`:@@CommonDurationDescriptionXHrXMinAbbr:${hours} hr ${minutes} min`;
        }

        if (hours) {
            return $localize`:@@CommonDurationDescriptionXHrAbbr:${hours} hr`;
        }

        if (minutes) {
            return $localize`:@@CommonDurationDescriptionXMinAbbr:${minutes} min`;
        }

        return $localize`:@@CommonZeroMin:0 min`;
    }

    static Minutes2Format_Abbr(mins: number) {
        const hours = Math.floor(mins / 60);
        const minutes = (mins % 60).toString().padStart(2, '0');
        return $localize`:@@CommonDurationDescriptionXHXMAbbr:${hours}h ${minutes}m`;
    }

    private static AddLeadingZeroToSingleMinutesAndSeconds(n: number) {
        if (n < 0) {
            throw new Error('Negative Time Exception');
        }
        return n > 9 ? '' + n : '0' + n;
    }

    static Seconds2Format_HHMMSS(seconds: number) {
        if (seconds < 0) {
            throw new Error('Negative Time Exception');
        } else {
            let format = '';
            const hours = Math.floor(seconds / 3600);
            const leftHours = seconds - (hours * 3600);
            const mins = Math.floor(leftHours / 60);
            const leftMins = leftHours - (mins * 60);
            const secs = Math.floor(leftMins)
            format = '' + hours + ':' + this.AddLeadingZeroToSingleMinutesAndSeconds(mins) + ':' + this.AddLeadingZeroToSingleMinutesAndSeconds(secs);
            return format;
        }
    }

    static Seconds2Format(seconds: number, format: SecondsOutputFormat = 'MM:SS') {
        if (seconds < 0) {
            throw new Error('Negative Time Exception');
        }

        if (format === 'HH:MM:SS') {
            const hours = Math.floor(seconds / 3600);
            const mins = Math.floor((seconds % 3600) / 60);
            const secs = Math.floor((seconds % 3600) % 60);
            if (hours > 0) {
                return hours + ':' + this.AddLeadingZeroToSingleMinutesAndSeconds(mins) + ':' + this.AddLeadingZeroToSingleMinutesAndSeconds(secs);
            }
            return mins + ':' + this.AddLeadingZeroToSingleMinutesAndSeconds(secs)
        }
        const mins = Math.floor(seconds / 60);
        const leftMins = Math.floor(seconds - (mins * 60));
        return mins + ':' + this.AddLeadingZeroToSingleMinutesAndSeconds(leftMins);
    }

    static Minutes2Format_MMSS(minutes:number) {
        return FuncMinutes2Format.Seconds2Format(minutes * 60, 'MM:SS');
    }

    static Hours2Format_MMSS(hours:number) {
        return FuncMinutes2Format.Seconds2Format(hours * 3600, 'MM:SS');
    }

    static Minutes2Format_HHMMSS(minutes:number) {
        return FuncMinutes2Format.Seconds2Format_HHMMSS(minutes * 60);
    }

    static Hours2Format_HHMMSS(hours:number) {
        return FuncMinutes2Format.Seconds2Format_HHMMSS(hours * 3600);
    }

    static Days2Format_HHMMSS(days:number) {
        return FuncMinutes2Format.Seconds2Format_HHMMSS(days * 86400);
    }

    static RenderSeconds(value: number, format: SecondsOutputFormat = 'MM:SS'): string {
        if (format) {
            return FuncMinutes2Format.Seconds2Format(value, format);
        }
        throw new Error(`Parsing of format ${format} is unimplemented`);
    }

    static RenderMinutes(value: number, format: MinutesOutputFormat = 'HH:MM'): string {
        switch (format) {
            case 'HH:MM': return FuncMinutes2Format.Minutes2Format_HH_MM(value);
            case 'MM:SS': return FuncMinutes2Format.Minutes2Format_MMSS(value);
            case 'HH:MM:SS': return FuncMinutes2Format.Minutes2Format_HHMMSS(value);
            case 'long': return FuncMinutes2Format.Minutes2Format_Long(value);
            case 'short': return FuncMinutes2Format.Minutes2Format_Short(value);
            case 'abbr': return FuncMinutes2Format.Minutes2Format_Abbr(value);
            default: throw new Error(`Parsing of format ${format} is unimplemented`);
        }
    }

    static RenderHours(value: number, format: HoursOutputFormat = 'MM:SS'): string {
        switch (format) {
            case 'MM:SS': return FuncMinutes2Format.Hours2Format_MMSS(value);
            case 'HH:MM:SS': return FuncMinutes2Format.Hours2Format_HHMMSS(value);
            default: throw new Error(`Parsing of format ${format} is unimplemented`);
        }
    }

    static RenderDays(value: number, format: DaysOutputFormat = 'HH:MM:SS'): string {
        switch (format) {
            case 'HH:MM:SS': return FuncMinutes2Format.Days2Format_HHMMSS(value);
            default: throw new Error(`Parsing of format ${format} is unimplemented`);
        }
    }
}
